import React, { useMemo } from 'react';
import { useCurrentFrame } from 'remotion';

interface WaveformProps {
  textColor: string;
  height: number;
  loopEveryNFrames?: number;
  minScale?: number;
  offset?: number;
}

const RemotionWaveform = ({
  textColor,
  height,
  loopEveryNFrames = 120,
  minScale = 0.5, // prevent disappearing bars
  offset = 0
}: WaveformProps): JSX.Element => {
  const frame = useCurrentFrame() + offset;

  // Helper function to calculate the scale of each bar
  const getScale = (barIndex: number) => {
    const phase = getPhase(barIndex);
    return calculateScale(phase);
  };

  // Memoize the centerY value to avoid unnecessary recalculations
  const centerY = useMemo(() => 38.05 / 2, []);

  // Calculate phase based on current frame and bar index
  const getPhase = (barIndex: number): number => {
    const t = (frame + barIndex * 7) % loopEveryNFrames;
    return (2 * Math.PI * t) / loopEveryNFrames;
  };

  // Calculate scale based on phase and minimum scale
  const calculateScale = (phase: number): number => {
    const amplitude = 1 - minScale;
    return minScale + amplitude * Math.abs(Math.sin(phase));
  };

  // Render SVG elements for each bar
  return (
    <div className="flex h-full items-center justify-center" style={{ height: `${height}px`, width: `${height}px` }}>
      <svg
        style={{
          fill: textColor,
          height: `${height}px`,
          width: `${height}px`
        }}
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 38.05 38.05"
      >
        <title>Audio Wave</title>
        {generatePaths().map((path, index) => {
          const scaleY = getScale(index);
          return (
            <g
              key={path.id}
              transform={`
                translate(0, ${centerY})
                scale(1, ${scaleY})
                translate(0, -${centerY})
              `}
            >
              <path d={path.d} />
            </g>
          );
        })}
      </svg>
    </div>
  );
};

// Helper function to generate all paths for the waveforms
const generatePaths = () => [
  { id: 1, d: 'M0.91,15L0.78,15A1,1,0,0,0,0,16v6a1,1,0,1,0,2,0s0,0,0,0V16a1,1,0,0,0-1-1H0.91Z' },
  { id: 2, d: 'M6.91,9L6.78,9A1,1,0,0,0,6,10V28a1,1,0,1,0,2,0s0,0,0,0V10A1,1,0,0,0,7,9H6.91Z' },
  { id: 3, d: 'M12.91,0L12.78,0A1,1,0,0,0,12,1V37a1,1,0,1,0,2,0s0,0,0,0V1a1,1,0,0,0-1-1H12.91Z' },
  { id: 4, d: 'M18.91,10l-0.12,0A1,1,0,0,0,18,11V27a1,1,0,1,0,2,0s0,0,0,0V11a1,1,0,0,0-1-1H18.91Z' },
  { id: 5, d: 'M24.91,15l-0.12,0A1,1,0,0,0,24,16v6a1,1,0,0,0,2,0s0,0,0,0V16a1,1,0,0,0-1-1H24.91Z' },
  { id: 6, d: 'M30.91,10l-0.12,0A1,1,0,0,0,30,11V27a1,1,0,1,0,2,0s0,0,0,0V11a1,1,0,0,0-1-1H30.91Z' }
];

export default RemotionWaveform;
